/**
 * Оборонительное чтение словарей карточки.
 *
 * `core`, `email`, `phone`, `session` приезжают словарём **имён колонок
 * выгрузки** с неизвестным типом значения: FastAPI отдаёт `Decimal` числом,
 * `datetime` — строкой ISO, `Integer` — числом, а колонок-текста большинство.
 * Читаем через одни ворота и приводим к строке или `null`; «колонка пуста» и
 * «ключа нет» неотличимы и оба дают `null` — экран поставит на это место «—»,
 * а не выдуманное значение (§7 AMEND-10).
 */

export type Dict = Record<string, unknown> | null | undefined

/** Значение колонки строкой или `null`. Пустая строка — тоже `null`. */
export function field(source: Dict, key: string): string | null {
  if (!source) return null
  const value = source[key]
  if (value === null || value === undefined) return null
  if (typeof value === "string") return value.trim() === "" ? null : value
  if (typeof value === "number" || typeof value === "bigint") return String(value)
  if (typeof value === "boolean") return value ? "true" : "false"
  // Массив/объект в плоской колонке не ждём; молча его не разворачиваем.
  return null
}

/**
 * Числовое значение как есть (строкой или числом) — под `formatScore` и
 * `formatPercent`, которым всё равно, что придёт. `numeric` в Postgres уезжает
 * в JSON строкой, и приводить её к `number` здесь значило бы потерять точность
 * раньше, чем это сделает форматтер.
 */
export function numeric(source: Dict, key: string): string | number | null {
  if (!source) return null
  const value = source[key]
  if (typeof value === "number") return value
  if (typeof value === "string") return value.trim() === "" ? null : value
  return null
}

const TRUTHY = new Set(["true", "1", "yes", "y", "t", "on"])
const FALSY = new Set(["false", "0", "no", "n", "f", "off"])

/**
 * Флаг колонки: `true` / `false` / `null`.
 *
 * Флаги выгрузки лежат текстом (`"true"`, `"1"`, `"false"`, пусто), и три
 * состояния разводятся намеренно. `null` — «неизвестно»: колонки нет или она
 * пуста. Рисовать на его месте «нет» нельзя — это утверждение, которого в
 * данных не было. Незнакомую строку тоже отдаём `null`, а не угадываем.
 */
export function flagOf(source: Dict, key: string): boolean | null {
  const raw = field(source, key)
  if (raw === null) return null
  const norm = raw.trim().toLowerCase()
  if (TRUTHY.has(norm)) return true
  if (FALSY.has(norm)) return false
  return null
}

/** Профили, зарегистрированные на почту (§5, блок Email → Registered profiles). */
export const EMAIL_PROFILES: ReadonlyArray<readonly [string, string]> = [
  ["email_facebook_registered", "Facebook"],
  ["email_google_registered", "Google"],
  ["email_apple_registered", "Apple"],
  ["email_twitter_registered", "Twitter"],
  ["email_microsoft_registered", "Microsoft"],
  ["email_yahoo_registered", "Yahoo"],
  ["email_instagram_registered", "Instagram"],
  ["email_linkedin_registered", "LinkedIn"],
  ["email_github_registered", "GitHub"],
]

/** Профили, зарегистрированные на телефон (§5, блок Phone → Registered profiles). */
export const PHONE_PROFILES: ReadonlyArray<readonly [string, string]> = [
  ["phone_facebook_registered", "Facebook"],
  ["phone_google_registered", "Google"],
  ["phone_whatsapp_registered", "WhatsApp"],
  ["phone_telegram_registered", "Telegram"],
  ["phone_viber_registered", "Viber"],
]

export interface ProfileState {
  label: string
  on: boolean | null
}

/**
 * Состояние набора профилей. `null` (колонки нет / пусто) отсеивается вызовом
 * на экране: неизвестный профиль не должен рисоваться как «не зарегистрирован».
 */
export function profiles(
  source: Dict,
  entries: ReadonlyArray<readonly [string, string]>,
): ProfileState[] {
  return entries.map(([key, label]) => ({ label, on: flagOf(source, key) }))
}

/** Флаги IP-адреса (§5, блок IP information). Порядок — как в образце. */
export const IP_FLAGS: ReadonlyArray<readonly [string, string]> = [
  ["public_proxy", "Public proxy"],
  ["data_center_proxy", "Data center proxy"],
  ["residential_proxy", "Residential proxy"],
  ["consumer_privacy_network", "Consumer privacy network"],
  ["enterprise_private_network", "Enterprise private network"],
  ["search_engine_spider", "Search engine spider"],
  ["harmful", "Harmful"],
]
